import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { Truck, ArrowLeft, CheckCircle, Star, Lightning, Rocket, Tag, XCircle } from "@phosphor-icons/react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

const PLAN_ICONS = { starter: Star, pro: Lightning, scale: Rocket };
// Filter out "OTHER" — courier companies require a real ISO-2 code
const COURIER_COUNTRIES = COUNTRIES.filter((c) => c.code !== "OTHER");

export default function SignupCourier() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    company_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    admin_password_confirm: "",
    phone: "",
    address: "",
    country: "JM",
    plan: params.get("plan") || "starter",
    promo_code: params.get("promo") || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Promo validation state — debounced check fires on plan or code change.
  const [promoState, setPromoState] = useState({ status: "idle", result: null, error: null });

  useEffect(() => {
    axios.get(`${BACKEND}/api/billing/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  // Validate the promo whenever the code or plan changes (debounced).
  useEffect(() => {
    const code = form.promo_code.trim();
    if (!code) {
      setPromoState({ status: "idle", result: null, error: null });
      return;
    }
    setPromoState({ status: "checking", result: null, error: null });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${BACKEND}/api/public/promotions/validate`, {
          code, plan_id: form.plan,
        });
        setPromoState({ status: "valid", result: data, error: null });
      } catch (e) {
        const detail = e.response?.data?.detail || "Invalid promo code";
        setPromoState({ status: "invalid", result: null, error: typeof detail === "string" ? detail : "Invalid promo code" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.promo_code, form.plan]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.address.trim()) {
      toast.error("Company address is required");
      return;
    }
    if (form.admin_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (form.admin_password !== form.admin_password_confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const { admin_password_confirm, ...payload } = form;
      // Drop the promo_code field entirely when blank to avoid the validator hitting an empty string.
      if (!payload.promo_code) delete payload.promo_code;
      const { data } = await axios.post(`${BACKEND}/api/public/signup/courier`, payload);
      setSuccess(data);
      toast.success("Account created — check your email to verify");
    } catch (err) {
      const detail = err.response?.data?.detail || "Signup failed";
      toast.error(typeof detail === "string" ? detail : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="signup-courier-success">
        <div className="max-w-lg w-full bg-white border border-zinc-200 rounded-xl p-8 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-5">
            <CheckCircle size={26} weight="bold" className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: "Work Sans" }}>You're almost in!</h1>
          <p className="text-zinc-600 mb-6">
            We've created your courier account. To activate it:
          </p>
          <ol className="space-y-3 text-sm text-zinc-800 mb-6">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-zinc-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">1</span><span><b>Verify your email</b> — we just sent you a link.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-zinc-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">2</span><span><b>Sign in</b> with the password you just chose.</span></li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-zinc-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">3</span><span><b>Pay your initial subscription invoice</b> of <b>${success.amount_due_usd?.toFixed(2)} USD</b> via WiPay to unlock the dashboard.</span></li>
          </ol>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-800 mb-1">Your customer signup URL</div>
            <code className="text-xs text-blue-900 break-all font-mono">{success.customer_signup_url}</code>
            <div className="text-xs text-blue-700 mt-2">Share this link with your end customers so they can sign up under your company and track their packages.</div>
          </div>
          <Link to="/login">
            <Button className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="signup-success-signin-btn">Continue to sign in </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="signup-courier-page">
      <Toaster position="top-right" />
      <div className="max-w-[1100px] mx-auto px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-8" data-testid="back-to-landing">
          <ArrowLeft size={14} /> Back to Kanal
        </Link>
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10">
          {/* Left intro */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#002BF6]/10 border border-[#002BF6]/20 text-[#002BF6] text-[11px] uppercase tracking-[0.18em] font-semibold mb-6">
              <Truck size={11} weight="bold" /> Courier signup
            </div>
            <h1 className="text-4xl font-semibold tracking-tight mb-4 leading-tight" style={{ fontFamily: "Work Sans" }}>
              Start running your courier business on Kanal in minutes.
            </h1>
            <p className="text-zinc-600 mb-8">
              Create your tenant workspace, get a permanent public customer signup URL, and start managing packages today.
            </p>
            <div className="space-y-3">
              {["Multi-role team & RBAC", "WiPay-powered invoicing", "Auto driver assignment", "Air / sea / ground manifests", "Branded PDFs & emails"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-zinc-700">
                  <CheckCircle size={15} weight="bold" className="text-emerald-600" /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="bg-white border border-zinc-200 rounded-xl p-7 shadow-sm space-y-5"
            data-testid="signup-courier-form"
          >
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans" }}>Create your courier account</h2>
              <Link to="/login" className="text-xs text-[#002BF6] hover:underline">Have an account?</Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Company name" testid="signup-company-name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} required />
              <Field label="Company phone (optional)" testid="signup-company-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                Company address <span className="text-red-500 ml-0.5">*</span>
              </Label>
              <div className="mt-1.5">
                <AddressAutocomplete
                  value={form.address}
                  onChange={(v) => setForm({ ...form, address: v })}
                  country={form.country || "JM"}
                  placeholder={form.country === "JM" ? "Start typing a Jamaican address…" : "Start typing your company address…"}
                  rows={2}
                  dataTestId="signup-company-address"
                  className="text-sm"
                />
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">
                Start typing — suggestions are filtered to {form.country === "JM" ? "Jamaica" : "your selected country"}. Shown on invoices, manifests, and your customer signup page.
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Country</Label>
              <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger className="mt-1.5" data-testid="signup-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COURIER_COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`signup-country-${c.code}`}>
                      {c.flag} {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field label="Your name" testid="signup-admin-name" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} required />
            <Field label="Your email (admin login)" type="email" testid="signup-admin-email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} required />
            <Field label="Choose a password (min 6 chars)" type="password" testid="signup-admin-password" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} required />
            <Field label="Confirm password" type="password" testid="signup-admin-password-confirm" value={form.admin_password_confirm} onChange={(v) => setForm({ ...form, admin_password_confirm: v })} required />

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Plan</Label>
              <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                <SelectTrigger className="mt-1.5" data-testid="signup-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => {
                    const Icon = PLAN_ICONS[p.id] || Star;
                    return (
                      <SelectItem key={p.id} value={p.id} data-testid={`signup-plan-option-${p.id}`}>
                        <div className="flex items-center gap-2">
                          <Icon size={13} weight="bold" />
                          <span className="font-medium">{p.name}</span>
                          <span className="text-xs text-zinc-500">${p.price} USD/mo</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1.5">You can change your plan any time. Account unlocks once the initial invoice is paid.</p>
            </div>

            {/* Promo code — surfaces a discount on the initial subscription invoice. */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 flex items-center gap-1.5">
                <Tag size={11} weight="bold" /> Promo code <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional)</span>
              </Label>
              <Input
                type="text"
                value={form.promo_code}
                onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                placeholder="e.g. LAUNCH50"
                className="mt-1.5 font-mono"
                data-testid="signup-promo-input"
              />
              {form.promo_code && promoState.status === "checking" && (
                <p className="text-xs text-zinc-500 mt-1.5" data-testid="signup-promo-checking">Checking…</p>
              )}
              {promoState.status === "valid" && promoState.result && (() => {
                const plan = plans.find((p) => p.id === form.plan);
                const original = plan?.price ?? 0;
                const discounted = (original * (100 - promoState.result.discount_pct) / 100).toFixed(2);
                return (
                  <div
                    className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs"
                    data-testid="signup-promo-valid"
                  >
                    <div className="flex items-start gap-2 text-emerald-900">
                      <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold">
                          {promoState.result.discount_pct}% off — initial invoice ${discounted} (was ${original.toFixed(2)})
                        </div>
                        {promoState.result.description && (
                          <div className="text-emerald-800/80 mt-0.5">{promoState.result.description}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {promoState.status === "invalid" && (
                <p
                  className="text-xs text-red-600 mt-1.5 inline-flex items-center gap-1"
                  data-testid="signup-promo-invalid"
                >
                  <XCircle size={11} weight="fill" /> {promoState.error}
                </p>
              )}
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white h-11" data-testid="signup-submit-btn">
              {submitting ? "Creating…" : "Create courier account "}
            </Button>
            <p className="text-[11px] text-zinc-500 text-center">
              By signing up you agree to receive a verification email and pay your initial subscription via WiPay or Bank transfer.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required = false }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <Input type={type} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}
